'use client';
import { createUpgradeAssessment } from 'api/frontend';
import { Cluster, UpgradeAssessment } from 'api/models';
import { ConsoleLoader } from 'components/Loader';
import Page from 'components/Page';
import { RouterLink } from 'components/RouterLink';
import { AtomicTooltip } from 'components/design-system';
import BaseTable from 'components/design-system/Table/BaseTable';
import { somethingWentWrong, toastAutoHideDuration } from 'constants/toasts';
import useAccountIdRoute from 'hooks/useAccountIdRoute';
import useUserAccountState from 'hooks/useUserAccountState';
import { useSnackbar } from 'notistack';
import { SyntheticEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import {
  Badge,
  Breadcrumbs,
  Button,
  RadioGroup,
  Select,
  StepBase,
} from 'subframe/index';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import styles from '../UpgradeTemplatesRequestTemplate.module.scss';
import { getClusterProvider, getClusterRegion } from 'utils/clusterStates';
import { useAvailableK8sVersionsForUpgrade } from 'hooks/useAvailableK8sVersionsForUpgrade';
import {
  useListClustersInfinite,
  useListUpgradeAssessmentsInfinite,
} from 'api/frontend-infinite';
import { clusterFlattenPages } from 'utils/arrays';
import { Searchbar } from 'components/design-system/Searchbar';
import { useDebounce } from 'react-use';
import semver from 'semver';
import { isLatestVersion } from 'utils/upgrades';

export default function UpgradeAssessmentRequest() {
  const { logEvent } = AnalyticsEventLogger();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { account } = useUserAccountState();
  const clusterUpgradeAssessmentsBasePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/upgrades/assessments',
  );
  const clustersBasePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/clusters',
  );

  const { mutate } = useListUpgradeAssessmentsInfinite(
    {},
    {
      request: {
        headers: { Authorization: `Bearer ${account.token}` },
      },
    },
  );

  const { data: clustersList, isLoading: isLoadingClustersList } =
    useListClustersInfinite(
      '',
      { filter: ['status:active'] },
      {
        request: { headers: { Authorization: `Bearer ${account?.token}` } },
      },
      {
        initialSize: 100,
        revalidateFirstPage: false,
      },
    );

  const clusterData = clusterFlattenPages(clustersList || []) || [];

  const realClusters = clusterData
    ? clusterData
        .filter(
          (c) =>
            c.internal_k8s_ref !== 'example-cluster-ref' &&
            c.last_scan_summary !== undefined,
        )
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    : [];
  const [selectedToVersion, setSelectedToVersion] = useState<string>();
  const [selectedCluster, setSelectedCluster] = useState<Cluster>(
    searchParams.get('cluster_id')
      ? realClusters.filter((c) => c.id === searchParams.get('cluster_id'))[0]
      : ({} as Cluster),
  );
  const displayData = searchParams.get('cluster_id')
    ? realClusters.filter((c) => c.id === searchParams.get('cluster_id'))
    : realClusters;

  const availableK8sVersions = useAvailableK8sVersionsForUpgrade(
    selectedCluster?.id ? selectedCluster : undefined,
  );

  const [requestAssessmentLoading, setRequestAssessmentLoading] =
    useState(false);

  const requestUpgradeAssessment = async function () {
    if (requestAssessmentLoading) {
      return;
    }

    setRequestAssessmentLoading(true);
    logEvent('upgrades-assessment-strategy-selected', {});
    if (selectedCluster === undefined || !selectedCluster.id) {
      enqueueSnackbar('Select a cluster to create an Upgrade Assessment', {
        variant: 'error',
        autoHideDuration: toastAutoHideDuration,
      });
      setRequestAssessmentLoading(false);
      return;
    }

    try {
      const createdUpgradeAssessment: UpgradeAssessment =
        await createUpgradeAssessment(
          {
            environment: selectedCluster.environment || '',
            resources: {
              clusters: [selectedCluster],
            },
            desired_version:
              selectedToVersion == 'Next Version'
                ? undefined
                : selectedToVersion
                ? `v${selectedToVersion}`
                : undefined,
          },
          { headers: { Authorization: `Bearer ${account.token}` } },
        );
      logEvent('clusters-upgrade-assessment-created', {
        upgrade_assessment_id: createdUpgradeAssessment?.id,
        upgrade_assessment_name: createdUpgradeAssessment?.name,
        cluster_id: selectedCluster.id,
      });
      await mutate();
      enqueueSnackbar(
        "Upgrade Assessment request successful. We'll notify you once it is ready. Note that this process may take up to 20 minutes",
        {
          variant: 'success',
          autoHideDuration: toastAutoHideDuration,
        },
      );
      setRequestAssessmentLoading(false);
      navigate(clusterUpgradeAssessmentsBasePath);
    } catch (err) {
      enqueueSnackbar(
        somethingWentWrong.replace(
          '<action>',
          'requesting this Upgrade Assessment',
        ),
        {
          variant: 'error',
          autoHideDuration: toastAutoHideDuration,
        },
      );
      setRequestAssessmentLoading(false);
    }
  };

  const [searchCluster, setSearchCluster] = useState('');
  const [clustersToShow, setClustersToShow] = useState(displayData);
  const handleOnChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: string,
  ) => {
    setSearchCluster(newValue);
  };
  useDebounce(
    () => {
      if (searchCluster) {
        logEvent('search-clusters-to-assess', { searchCluster });
      }
    },
    1000,
    [searchCluster],
  );
  useEffect(() => {
    // handle search
    if (clusterData !== undefined && !isLoadingClustersList) {
      if (searchCluster !== '') {
        const found = displayData.filter((item) =>
          item?.name?.toLowerCase().includes(searchCluster.toLowerCase()),
        );
        setClustersToShow(found);
      } else {
        setClustersToShow(displayData);

        // selected values based on search params
        setSelectedCluster(
          searchParams.get('cluster_id')
            ? realClusters.filter(
                (c) => c.id === searchParams.get('cluster_id'),
              )[0]
            : ({} as Cluster),
        );
      }
    }
  }, [clustersList, searchCluster]);

  return (
    <Page title="Upgrade Assessments">
      <div
        style={{ marginLeft: '32px', width: 'calc(100% - 32px' }}
        className={styles['pageContents']}
      >
        <Breadcrumbs>
          <Breadcrumbs.Item>Upgrade Copilot</Breadcrumbs.Item>
          <Breadcrumbs.Divider />
          <RouterLink to={clusterUpgradeAssessmentsBasePath}>
            <Breadcrumbs.Item>Upgrade Assessments</Breadcrumbs.Item>
          </RouterLink>
          <Breadcrumbs.Divider />
          <Breadcrumbs.Item active={true}>Request Assessment</Breadcrumbs.Item>
        </Breadcrumbs>
        <div className="flex w-full flex-col items-start gap-2">
          <div className="flex w-full items-center justify-end gap-2">
            <div className="flex grow shrink-0 basis-0 items-center gap-2">
              <div className="flex grow shrink-0 basis-0 flex-col items-start justify-end gap-2">
                <span className="text-section-header font-section-header text-default-font">
                  Request Upgrade Assessment
                </span>
                <span className="text-body font-body text-subtext-color">
                  Select a cluster that you would like to have assessed
                </span>
              </div>
            </div>
          </div>
        </div>

        <StepBase
          stepTitle="Select Cluster for Assessment"
          stepNumber="1"
          defaultOpen={true}
        >
          <div className="flex flex-col items-end">
            {displayData.length && !searchParams.get('cluster_id') ? (
              <Searchbar onChange={handleOnChange} />
            ) : (
              <></>
            )}
          </div>

          {isLoadingClustersList && <ConsoleLoader />}
          {!isLoadingClustersList &&
            clusterData !== undefined &&
            realClusters?.length === 0 && (
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-default-font">
                  No clusters onboarded so far. Click here to onboard your first
                  cluster
                </span>
                <RouterLink to={`${clustersBasePath}/new`}>
                  <Button
                    variant="brand-primary"
                    size="small"
                    icon="FeatherPlus"
                  >
                    Add Cluster
                  </Button>
                </RouterLink>
              </div>
            )}

          {clusterData !== undefined && realClusters.length > 0 && (
            <BaseTable
              settings={{
                localStorageKey: 'create_upgrade_assessments_table',
                dataCyPrefix: 'upgrades-create-assessment',
              }}
              data={clustersToShow}
              noMatchingData={Boolean(
                displayData.length && !clustersToShow.length,
              )}
              onRowClick={(cluster) => {
                setSelectedCluster(cluster);
              }}
              columns={[
                {
                  id: 'cluster',
                  title: 'Cluster',
                  titleStyle: { width: '35%' },
                  cellType: 'cell',
                  render: (cluster) => {
                    return (
                      <>
                        <RadioGroup>
                          <AtomicTooltip tooltipContent={cluster.name}>
                            <RadioGroup.Option
                              disabled={requestAssessmentLoading}
                              checked={selectedCluster?.id === cluster.id}
                              label={cluster.name}
                              value={cluster.id}
                            />
                          </AtomicTooltip>
                        </RadioGroup>
                      </>
                    );
                  },
                },
                {
                  id: 'environment',
                  title: 'Environment',
                  cellType: 'cell',
                  titleStyle: { width: '25%' },
                  render: (cluster) => {
                    if (cluster.environment) {
                      return (
                        <AtomicTooltip tooltipContent={cluster.environment}>
                          <Badge
                            variant="neutral"
                            style={{
                              maxWidth: '200px',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                            }}
                          >
                            {cluster.environment}
                          </Badge>
                        </AtomicTooltip>
                      );
                    } else {
                      return <></>;
                    }
                  },
                },
                {
                  id: 'version',
                  title: 'Current Version',
                  titleStyle: { width: '20%' },
                  cellType: 'cell',
                  render: (cluster) => {
                    return (
                      <span className="text-body font-body text-neutral-500">
                        {cluster.version}
                      </span>
                    );
                  },
                },
                {
                  id: 'provider-region',
                  title: 'Provider : Region',
                  titleStyle: { width: '20%' },
                  cellType: 'cell',
                  render: (cluster) => {
                    return (
                      <span className="text-body font-body text-neutral-500">
                        {`${getClusterProvider(cluster)} : ${getClusterRegion(
                          cluster,
                        )}`}
                      </span>
                    );
                  },
                },
              ]}
              actions={[]}
            />
          )}
        </StepBase>
        {/* Only show further content if clusters are available to create upgrade template */}
        {displayData.length ? (
          <>
            <StepBase
              stepTitle="Select Target Kubernetes Version"
              stepNumber="2"
              defaultOpen={true}
            >
              <div className="flex w-full flex-wrap items-center gap-6">
                <Select
                  label=""
                  placeholder="Select"
                  helpText=""
                  value={undefined}
                  onValueChange={(value: string) => {
                    setSelectedToVersion(value);
                  }}
                  disabled={requestAssessmentLoading || !selectedCluster?.id}
                >
                  {!isLatestVersion(selectedCluster?.version || '') && (
                    <Select.Item key="next" value="Next Version" />
                  )}
                  {availableK8sVersions.map((version) => (
                    <Select.Item key={version} value={version}>
                      {version}
                    </Select.Item>
                  ))}
                </Select>
              </div>
            </StepBase>
            <div className="flex w-full items-center gap-2">
              <Button
                size="large"
                icon="FeatherPlus"
                onClick={() => requestUpgradeAssessment()}
                loading={requestAssessmentLoading}
                disabled={selectedCluster?.id === undefined}
              >
                Request Assessment
              </Button>
            </div>
          </>
        ) : (
          <></>
        )}
      </div>
    </Page>
  );
}
